import type { IResponse } from '../types';
import type { AxiosInstance, AxiosRequestConfig } from 'axios';

class ApiClient {
    constructor(private axios: AxiosInstance, private commonConfig?: AxiosRequestConfig) {}

    async get<TOut>(url: string, config?: AxiosRequestConfig) : Promise<IResponse<TOut>> {
        try {
            const commonConfig: AxiosRequestConfig = this.commonConfig ?? {};

            const response = await this.axios({
                ...commonConfig,
                ...config,
                method: 'get',
                url,
            });
            return Utils.createResponse<TOut>(true, response.data);
        }
        catch(e: any) {
            if (e.response) {
                const response = e.response
                if(response && response.data) return Utils.createResponse(false, response.data)
            }
            return Utils.createResponse(false, e)
        }
    }

    async post<TIn, TOut>(url: string, data?: TIn | null, config?: AxiosRequestConfig) : Promise<IResponse<TOut>> {
        try {
            const commonConfig: AxiosRequestConfig = this.commonConfig ?? {};
            if(!data) data = null;

            const response = await this.axios({
                ...commonConfig,
                ...config,
                method: 'post',
                url,
                data,
            });
            return Utils.createResponse<TOut>(true, response.data);
        }
        catch(e: any) {
            if (e.response) {
                const response = e.response
                if(response && response.data) return Utils.createResponse(false, response.data)
            }
            return Utils.createResponse(false, e)
        }
    }

    async postFormData<TIn, TOut>(url: string, data?: TIn | null, config?: AxiosRequestConfig) : Promise<IResponse<TOut>> {
        try {
            const commonConfig: AxiosRequestConfig = this.commonConfig ?? {};
            if(!data) data = null;

            const response = await this.axios({
                ...commonConfig,
                ...config,
                method: 'post',
                url,
                data,
                headers: {
                    'Content-Type': 'multipart/form-data',
                }
            });
            return Utils.createResponse<TOut>(true, response.data);
        }
        catch(e: any) {
            if (e.response) {
                const response = e.response
                if(response && response.data) return Utils.createResponse(false, response.data)
            }
            return Utils.createResponse(false, e)
        }
    }

    async delete<TIn, TOut>(url: string, data?: TIn | null, config?: AxiosRequestConfig) : Promise<IResponse<TOut>> {
        try {
            const commonConfig: AxiosRequestConfig = this.commonConfig ?? {};
            if(!data) data = null;

            const response = await this.axios({
                ...commonConfig,
                ...config,
                method: 'delete',
                url,
                data,
            });
            return Utils.createResponse<TOut>(true, response.data);
        }
        catch(e: any) {
            if (e.response) {
                const response = e.response
                if(response && response.data) return Utils.createResponse(false, response.data)
            }
            return Utils.createResponse(false, e)
        }
    }

    async put<TIn, TOut>(url: string, data?: TIn | null, config?: AxiosRequestConfig) : Promise<IResponse<TOut>> {
        try {
            const commonConfig: AxiosRequestConfig = this.commonConfig ?? {};
            if(!data) data = null;

            const response = await this.axios({
                ...commonConfig,
                ...config,
                method: 'put',
                url,
                data,
            });
            return Utils.createResponse<TOut>(true, response.data);
        }
        catch(e: any) {
            if (e.response) {
                const response = e.response
                if(response && response.data) return Utils.createResponse(false, response.data)
            }
            return Utils.createResponse(false, e)
        }
    }

    async putFormData<TIn, TOut>(url: string, data?: TIn | null, config?: AxiosRequestConfig) : Promise<IResponse<TOut>> {
        try {
            const commonConfig: AxiosRequestConfig = this.commonConfig ?? {};
            if(!data) data = null;

            const response = await this.axios({
                ...commonConfig,
                ...config,
                method: 'put',
                url,
                data,
                headers: {
                    'Content-Type': 'multipart/form-data',
                }
            });
            return Utils.createResponse<TOut>(true, response.data);
        }
        catch(e: any) {
            if (e.response) {
                const response = e.response
                if(response && response.data) return Utils.createResponse(false, response.data)
            }
            return Utils.createResponse(false, e)
        }
    }

    async patch<TIn, TOut>(url: string, data?: TIn | null, config?: AxiosRequestConfig) : Promise<IResponse<TOut>> {
        try {
            const commonConfig: AxiosRequestConfig = this.commonConfig ?? {};
            if(!data) data = null;

            const response = await this.axios({
                ...commonConfig,
                ...config,
                method: 'patch',
                url,
                data,
            });
            return Utils.createResponse<TOut>(true, response.data);
        }
        catch(e: any) {
            if (e.response) {
                const response = e.response
                if(response && response.data) return Utils.createResponse(false, response.data)
            }
            return Utils.createResponse(false, e)
        }
    }
}

export const useApiClient = (config?: AxiosRequestConfig) => {
    const { $axios } = useNuxtApp();
    return new ApiClient($axios, config);
}
